/**
 * postMessage bridge for /embed/* when running inside an iframe.
 * Parent listens on window 'message'; validate event.data.source === EMBED_PARENT_MESSAGE_SOURCE.
 */

export const EMBED_PARENT_MESSAGE_SOURCE = 'ach-embed' as const;

export type EmbedParentActionKind = 'fetch' | 'create' | 'update' | 'delete';

export type EmbedParentMessage = EmbedParentApiActionMessage | EmbedParentRouteMessage;

export interface EmbedParentApiActionMessage {
    source: typeof EMBED_PARENT_MESSAGE_SOURCE;
    type: 'embed-api-action';
    action: EmbedParentActionKind;
    method: string;
    endpoint: string;
    status: number;
    timestamp: string;
}

export interface EmbedParentRouteMessage {
    source: typeof EMBED_PARENT_MESSAGE_SOURCE;
    type: 'embed-route';
    pathname: string;
    timestamp: string;
}

export function isEmbedParentMessagingActive(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    if (!window.location.pathname.startsWith('/embed')) {
        return false;
    }
    if (window.parent === window) {
        return false;
    }
    return true;
}

function postToParent(message: EmbedParentMessage): void {
    if (!isEmbedParentMessagingActive()) {
        return;
    }
    window.parent.postMessage(message, '*');
}

function methodToActionKind(method: string): EmbedParentActionKind {
    const m = method.toUpperCase();
    if (m === 'GET') {
        return 'fetch';
    }
    if (m === 'POST') {
        return 'create';
    }
    if (m === 'DELETE') {
        return 'delete';
    }
    return 'update';
}

/**
 * Call after a successful API response from the shared api client (embed iframe only).
 */
export function notifyEmbedParentApiAction(params: {
    method: string;
    endpoint: string;
    status: number;
}): void {
    const method = params.method.toUpperCase();
    postToParent({
        source: EMBED_PARENT_MESSAGE_SOURCE,
        type: 'embed-api-action',
        action: methodToActionKind(method),
        method,
        endpoint: params.endpoint,
        status: params.status,
        timestamp: new Date().toISOString(),
    });
}

/**
 * Call when the embed route changes (e.g. tags ↔ tag-types).
 */
export function notifyEmbedParentRoute(pathname: string): void {
    postToParent({
        source: EMBED_PARENT_MESSAGE_SOURCE,
        type: 'embed-route',
        pathname,
        timestamp: new Date().toISOString(),
    });
}
